<template>
  <div class="stock-linked-stock-actions stock-linked-section">
    <v-subheader class="stock-linked-section__title px-0 font-weight-bold">
      {{ title }}
    </v-subheader>
    <v-data-table
      :headers="headers"
      :items="items"
      hide-default-footer
      :items-per-page="-1"
      class="stock-linked-stock-actions__table stock-linked-table"
    >
      <template v-slot:item.code="{ item }">
        <router-link :to="actionRoute(item)">
          {{ item.code }}
        </router-link>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="receiptStatusColor(item.status)" dark>
          {{ actionStatusLabel(item) }}
        </v-chip>
      </template>
      <template v-slot:item.warehouse="{ item }">
        {{ warehouseLabel(item) }}
      </template>
      <template v-slot:item.counterparty="{ item }">
        {{ counterpartyLabel(item) }}
      </template>
      <template v-slot:item.action_date="{ item }">
        {{ item.action_date || "-" }}
      </template>
      <template v-slot:item.items_count="{ item }">
        {{ item.items_count != null ? item.items_count : "-" }}
      </template>
      <template v-slot:item.total_gross="{ item }">
        {{ formatMovementMoney(item.total_gross, item.currency_code) }}
      </template>
      <template v-slot:item.total_inventory_cost_net="{ item }">
        {{ formatFinancialValue(item.total_inventory_cost_net, item.currency_code) }}
      </template>
      <template v-slot:item.total_cost_of_goods_net="{ item }">
        {{ formatFinancialValue(item.total_cost_of_goods_net, item.currency_code) }}
      </template>
      <template v-slot:item.total_margin_net="{ item }">
        <span :class="marginTextClass(item.total_margin_net)">
          {{ formatFinancialValue(item.total_margin_net, item.currency_code) }}
        </span>
      </template>
      <template v-slot:item.margin_pct="{ item }">
        <span :class="marginTextClass(item.total_margin_net)">
          {{ movementMarginPct(item) }}
        </span>
      </template>
      <template v-slot:item.transport_order_code="{ item }">
        <router-link
          v-if="item.transport_order_id"
          :to="{ name: 'stock-transport-orders-view', params: { id: item.transport_order_id } }"
        >
          {{ item.transport_order_code }}
        </router-link>
        <span v-else>-</span>
      </template>
    </v-data-table>
    <v-alert v-if="!items.length" type="info" dense text class="mt-2">
      {{ emptyMessage }}
    </v-alert>
  </div>
</template>

<script>
import stockMovementRowDisplayMixin from "@/views/partials/stock/stockMovementRowDisplay.mixin";
import {
  buildLinkedDocumentHeaders,
  linkedReceiptFinancialHeaders,
  linkedSaleFinancialHeaders,
  linkedTransportOrderReferenceHeader,
} from "@/views/partials/stock/stockLinkedTableHeaders";

export default {
  name: "StockLinkedStockActionsTable",
  mixins: [stockMovementRowDisplayMixin],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      required: true,
    },
    emptyMessage: {
      type: String,
      required: true,
    },
    showTransportOrderColumn: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headers() {
      const headers = buildLinkedDocumentHeaders(this.$t.bind(this), {
        counterpartyLabelKey: this.counterpartyHeaderKey,
        includeTotal: false,
      });

      if (this.showReceiptFinancials) {
        headers.push(...linkedReceiptFinancialHeaders(this.$t.bind(this)));
      }
      if (this.showSaleFinancials) {
        headers.push(...linkedSaleFinancialHeaders(this.$t.bind(this)));
      }

      headers.push({
        text: this.$t("STOCK.SALES_ORDERS.TOTAL_GROSS"),
        value: "total_gross",
        sortable: false,
      });

      if (this.showTransportOrderColumn) {
        headers.push(linkedTransportOrderReferenceHeader(this.$t.bind(this)));
      }

      return headers;
    },
    showReceiptFinancials() {
      return (this.items || []).some((item) => item.kind === "receipt");
    },
    showSaleFinancials() {
      return (this.items || []).some((item) => item.kind === "delivery");
    },
    counterpartyHeaderKey() {
      const hasDelivery = (this.items || []).some((item) => item.kind === "delivery");
      const hasReceipt = (this.items || []).some((item) => item.kind === "receipt");

      if (hasDelivery && !hasReceipt) {
        return "STOCK.SALES_ORDERS.CUSTOMER";
      }
      if (hasReceipt && !hasDelivery) {
        return "STOCK.RECEIPTS.SUPPLIER";
      }
      return "STOCK.RECEIPTS.COUNTERPARTY";
    },
  },
  methods: {
    marginTextClass(value) {
      if (value == null || Number(value) === 0) {
        return "";
      }
      return Number(value) > 0 ? "success--text" : "error--text";
    },
    actionRoute(item) {
      if (item.kind === "delivery") {
        return { name: "stock-sales-view", params: { id: item.id } };
      }
      if (item.kind === "write_off") {
        return { name: "stock-write-offs-view", params: { id: item.id } };
      }
      if (item.kind === "transfer") {
        return { name: "stock-transfers-view", params: { id: item.id } };
      }
      return { name: "stock-receipts-view", params: { id: item.id } };
    },
    actionStatusLabel(item) {
      const prefix =
        item.kind === "delivery"
          ? "STOCK.STATUSES.SALE"
          : item.kind === "write_off"
            ? "STOCK.STATUSES.WRITE_OFF"
            : item.kind === "transfer"
              ? "STOCK.STATUSES.TRANSFER"
              : "STOCK.STATUSES.RECEIPT";
      const key = `${prefix}.${(item.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : item.status;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/views/partials/stock/stockLinkedTable.scss";

.stock-linked-stock-actions__table a {
  text-decoration: none;
  font-weight: 500;
}
.stock-linked-stock-actions__table a:hover {
  text-decoration: underline;
}
</style>
